import React, { useState, useMemo, useRef } from 'react';

const AssetFilters = ({ filters, onFilterChange, onReset, sites, departments, categories, locations = [], employees }) => {
  // Refs and timers for debounced inputs
  const filterTimers = useRef({});
  
  // Handle debounced filter changes
  const handleDebouncedFilterChange = (name, value) => {
    // Clear existing timer for this field
    if (filterTimers.current[name]) {
      clearTimeout(filterTimers.current[name]);
    }
    
    // Set new timer
    filterTimers.current[name] = setTimeout(() => {
      onFilterChange(name, value);
    }, 500);
  };
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    location: true,
    dates: true,
    financial: true
  });

  // Calculate active filters count
  const activeFiltersCount = useMemo(() => {
    return Object.values(filters).filter(value => value !== '' && value !== null && value !== undefined).length;
  }, [filters]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  return (
    <div className="asset-filters">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#2d3748' }}>Advanced Filters</h3>
          {activeFiltersCount > 0 && (
            <span style={{
              background: '#667eea',
              color: 'white',
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600
            }}>
              {activeFiltersCount} active
            </span>
          )}
        </div>
        <button
          onClick={onReset}
          style={{
            padding: '8px 16px',
            background: '#fed7d7',
            color: '#c53030',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '12px'
          }}
        >
          Reset Filters
        </button>
      </div>

      {/* Basic Information Section */}
      <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          onClick={() => toggleSection('basic')}
          style={{
            padding: '12px 16px',
            background: '#f7fafc',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: expandedSections.basic ? '1px solid #e2e8f0' : 'none'
          }}
        >
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>Basic Information</h4>
          <span style={{ fontSize: '18px', color: '#4a5568' }}>{expandedSections.basic ? '−' : '+'}</span>
        </div>
        {expandedSections.basic && (
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {/* Asset Tag */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Asset Tag ID
              </label>
              <input
                type="text"
                defaultValue={filters.assetTag || ''}
                onChange={(e) => handleDebouncedFilterChange('assetTag', e.target.value)}
                placeholder="Enter tag ID"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Description */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Description
              </label>
              <input
                type="text"
                defaultValue={filters.description || ''}
                onChange={(e) => handleDebouncedFilterChange('description', e.target.value)}
                placeholder="Enter description"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Brand */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Brand
              </label>
              <input
                type="text"
                defaultValue={filters.brand || ''}
                onChange={(e) => handleDebouncedFilterChange('brand', e.target.value)}
                placeholder="Enter brand"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Model */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Model
              </label>
              <input
                type="text"
                defaultValue={filters.model || ''}
                onChange={(e) => handleDebouncedFilterChange('model', e.target.value)}
                placeholder="Enter model"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Serial Number */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Serial Number
              </label>
              <input
                type="text"
                defaultValue={filters.serialNumber || ''}
                onChange={(e) => handleDebouncedFilterChange('serialNumber', e.target.value)}
                placeholder="Enter serial number"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Status */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Status
              </label>
              <select
                value={filters.status || ''}
                onChange={(e) => onFilterChange('status', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="in_transfer">In Transfer</option>
                <option value="received">Received</option>
                <option value="issue_reported">Issue Reported</option>
                <option value="available">Available</option>
              </select>
            </div>

            {/* Condition */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Condition
              </label>
              <select
                value={filters.condition || ''}
                onChange={(e) => onFilterChange('condition', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All Conditions</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
                <option value="damaged">Damaged</option>
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Location & Assignment Section */}
      <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          onClick={() => toggleSection('location')}
          style={{
            padding: '12px 16px',
            background: '#f7fafc',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: expandedSections.location ? '1px solid #e2e8f0' : 'none'
          }}
        >
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>Location & Assignment</h4>
          <span style={{ fontSize: '18px', color: '#4a5568' }}>{expandedSections.location ? '−' : '+'}</span>
        </div>
        {expandedSections.location && (
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {/* Site */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Site
              </label>
              <select
                value={filters.site || ''}
                onChange={(e) => onFilterChange('site', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All Sites</option>
                {sites.map(site => (
                  <option key={site.id || site._id} value={site.id || site._id}>
                    {site.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Location */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Location
              </label>
              <select
                value={filters.location || ''}
                onChange={(e) => onFilterChange('location', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All Locations</option>
                {locations.map(loc => (
                  <option key={loc.id || loc._id} value={loc.name}>
                    {loc.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Category */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Category
              </label>
              <select
                value={filters.category || ''}
                onChange={(e) => onFilterChange('category', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All Categories</option>
                {categories.map(cat => (
                  <option key={cat.id || cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Department */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Department
              </label>
              <select
                value={filters.department || ''}
                onChange={(e) => onFilterChange('department', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All Departments</option>
                {departments.map(dept => (
                  <option key={dept.id || dept._id} value={dept.id || dept._id}>
                    {dept.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Assigned To */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Assigned To
              </label>
              <select
                value={filters.assignedTo || ''}
                onChange={(e) => onFilterChange('assignedTo', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              >
                <option value="">All People</option>
                {employees.map(emp => (
                  <option key={emp.id || emp._id} value={emp.id || emp._id}>
                    {emp.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Date Ranges Section */}
      <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          onClick={() => toggleSection('dates')}
          style={{
            padding: '12px 16px',
            background: '#f7fafc',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: expandedSections.dates ? '1px solid #e2e8f0' : 'none'
          }}
        >
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>Date Ranges</h4>
          <span style={{ fontSize: '18px', color: '#4a5568' }}>{expandedSections.dates ? '−' : '+'}</span>
        </div>
        {expandedSections.dates && (
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {/* Purchase Date From */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Purchase Date From
              </label>
              <input
                type="date"
                value={filters.purchaseDateFrom || ''}
                onChange={(e) => onFilterChange('purchaseDateFrom', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Purchase Date To */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Purchase Date To
              </label>
              <input
                type="date"
                value={filters.purchaseDateTo || ''}
                onChange={(e) => onFilterChange('purchaseDateTo', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Assigned Date From */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Assigned Date From
              </label>
              <input
                type="date"
                value={filters.assignedDateFrom || ''}
                onChange={(e) => onFilterChange('assignedDateFrom', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Assigned Date To */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Assigned Date To
              </label>
              <input
                type="date"
                value={filters.assignedDateTo || ''}
                onChange={(e) => onFilterChange('assignedDateTo', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Expected Return Date From */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Expected Return Date From
              </label>
              <input
                type="date"
                value={filters.expectedReturnDateFrom || ''}
                onChange={(e) => onFilterChange('expectedReturnDateFrom', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Expected Return Date To */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Expected Return Date To
              </label>
              <input
                type="date"
                value={filters.expectedReturnDateTo || ''}
                onChange={(e) => onFilterChange('expectedReturnDateTo', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Created Date From */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Created Date From
              </label>
              <input
                type="date"
                value={filters.createdAtFrom || ''}
                onChange={(e) => onFilterChange('createdAtFrom', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Created Date To */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Created Date To
              </label>
              <input
                type="date"
                value={filters.createdAtTo || ''}
                onChange={(e) => onFilterChange('createdAtTo', e.target.value)}
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Financial Section */}
      <div style={{ marginBottom: '20px', border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
        <div
          onClick={() => toggleSection('financial')}
          style={{
            padding: '12px 16px',
            background: '#f7fafc',
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: expandedSections.financial ? '1px solid #e2e8f0' : 'none'
          }}
        >
          <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#2d3748' }}>Financial Information</h4>
          <span style={{ fontSize: '18px', color: '#4a5568' }}>{expandedSections.financial ? '−' : '+'}</span>
        </div>
        {expandedSections.financial && (
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
            {/* Cost From */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Cost From (₹)
              </label>
              <input
                type="number"
                defaultValue={filters.costFrom || ''}
                onChange={(e) => handleDebouncedFilterChange('costFrom', e.target.value)}
                placeholder="0"
                step="0.01"
                min="0"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Cost To */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Cost To (₹)
              </label>
              <input
                type="number"
                defaultValue={filters.costTo || ''}
                onChange={(e) => handleDebouncedFilterChange('costTo', e.target.value)}
                placeholder="0"
                step="0.01"
                min="0"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>

            {/* Purchased From */}
            <div>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '12px', fontWeight: 500, color: '#4a5568' }}>
                Purchased From
              </label>
              <input
                type="text"
                defaultValue={filters.purchasedFrom || ''}
                onChange={(e) => handleDebouncedFilterChange('purchasedFrom', e.target.value)}
                placeholder="Enter vendor"
                autoComplete="off"
                style={{
                  width: '100%',
                  padding: '8px 10px',
                  border: '2px solid #e2e8f0',
                  borderRadius: '6px',
                  fontSize: '13px'
                }}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default AssetFilters;
